import React from 'react'
import { Switch } from 'antd'
import i from '../translations/i'
import { storeLocal } from '../helpers/stores'
import { ErrorByPricingPlan } from './ErrorByPricingPlan'

// Цветной бейдж-заголовок: иконка в полупрозрачном квадрате + жирный текст на заданном фоне.
// Это тот самый элемент "Собственная доставка" — вынесен отдельно, чтобы использовать
// как заголовок SectionCard, так и отдельно, где нужен просто цветной лейбл с иконкой.
export const SectionBadge = ({ icon, text, background, color = '#fff', toggle }: {
    icon: React.ReactNode
    text: React.ReactNode
    background: string
    color?: string
    toggle?: React.ReactNode
}) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', padding: '14px 18px', borderRadius: 14,
        background
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.18)', color, fontSize: 18
            }}>
                {icon}
            </div>
            <span style={{ fontSize: 17, fontWeight: 700, color }}>{text}</span>
        </div>
        {toggle}
    </div>
)

// Затемняет группу полей, которые реально зависят от переключателя, но сами
// переключателем не являются (в отличие от главного тумблера в MasterToggle).
export const DimGroup = ({ active, children }: { active: boolean, children: React.ReactNode }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', gap: 16, width: '100%',
        opacity: active ? 1 : 0.5,
        transition: 'opacity .15s ease'
    }}>
        {children}
    </div>
)

export const MasterToggle = ({ children }: { children: React.ReactNode }) => (
    <div style={{
        border: '1px solid var(--border-light-color)',
        borderRadius: 12, padding: '4px 16px', width: '100%'
    }}>
        {children}
    </div>
)

// plan — минимальный тариф: на тарифе ниже тумблер заблокирован, а при наведении
// всплывает подсказка ErrorByPricingPlan.
export const ToggleField = ({ icon, label, checked, onChange, disabled = false, plan }: {
    icon?: React.ReactNode
    label: React.ReactNode
    checked: boolean
    onChange: (v: boolean) => void
    disabled?: boolean
    plan?: number
}) => {
    const locked = plan !== undefined && storeLocal.get('pricing_plan') < plan
    const toggle = <Switch checkedChildren={i.t('def:on')} unCheckedChildren={i.t('def:off')} checked={checked} onChange={onChange} disabled={disabled || locked} />

    return (
        <div style={{
            background: 'var(--surface-field-color)', borderRadius: 12, padding: '10px 16px', width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            opacity: disabled || locked ? 0.5 : 1
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {icon &&
                    <span style={{
                        width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(114,100,214,0.12)', color: 'var(--primary-color)', fontSize: 12
                    }}>
                        {icon}
                    </span>
                }
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)' }}>{label}</span>
            </div>
            {plan !== undefined ? <ErrorByPricingPlan plan={plan} content={toggle} /> : toggle}
        </div>
    )
}

// Простое поле без иконки и фоновой плашки — только подпись + контент.
export const PlainField = ({ label, children }: { label: React.ReactNode, children: React.ReactNode }) => (
    <div style={{ width: '100%' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary-color)', marginBottom: 6 }}>
            {label}
        </div>
        {children}
    </div>
)

// Строка-тумблер без иконки/фона — подпись слева, свитч справа.
export const PlainToggle = ({ label, checked, onChange, disabled = false }: {
    label: React.ReactNode
    checked: boolean
    onChange: (v: boolean) => void
    disabled?: boolean
}) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        width: '100%', opacity: disabled ? 0.5 : 1
    }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)' }}>{label}</span>
        <Switch checkedChildren={i.t('def:on')} unCheckedChildren={i.t('def:off')} checked={checked} onChange={onChange} disabled={disabled} />
    </div>
)

export const FieldBlock = ({ icon, label, children }: { icon?: React.ReactNode, label: React.ReactNode, children: React.ReactNode }) => (
    <div style={{ background: 'var(--surface-field-color)', borderRadius: 12, padding: '12px 16px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {icon &&
                <span style={{
                    width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(114,100,214,0.12)', color: 'var(--primary-color)', fontSize: 12
                }}>
                    {icon}
                </span>
            }
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary-color)' }}>{label}</span>
        </div>
        {children}
    </div>
)
